import { API } from '@/config/api';
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import AdminNavigation from '@/components/AdminNavigation';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Shield, UserPlus, Edit2 as Edit, Trash2, Loader2, Lock } from 'lucide-react';
import { useConfirm } from '@/components/ConfirmDialog';

export default function AdminAdministrators() {
  const { user } = useAuth();
  const confirmDialog = useConfirm();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', auth_provider: 'local', skip_2fa: false, is_active: true });

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      setAdmins((res.data || []).filter(u => u.role === 'admin'));
    } catch { setAdmins([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const resetForm = () => setFormData({ name: '', email: '', password: '', auth_provider: 'local', skip_2fa: false, is_active: true });

  const handleAdd = async () => {
    if (!formData.name || !formData.email) { toast.error('Namn og e-post er påkrevd'); return; }
    if (formData.auth_provider === 'local' && !formData.password) { toast.error('Passord er påkrevd'); return; }
    try {
      const data = { name: formData.name, email: formData.email, role: 'admin', auth_provider: formData.auth_provider, skip_2fa: formData.skip_2fa };
      if (formData.auth_provider === 'local') data.password = formData.password;
      await axios.post(`${API}/users`, data, { withCredentials: true });
      toast.success('Administrator oppretta');
      setShowAddDialog(false); resetForm(); fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Kunne ikkje opprette'); }
  };

  const openEdit = (au) => {
    setEditingUser(au);
    setFormData({ name: au.name || '', email: au.email || '', password: '', auth_provider: au.auth_provider || 'local', skip_2fa: au.skip_2fa || false, is_active: au.is_active !== false });
    setShowEditDialog(true);
  };

  const handleEdit = async () => {
    if (!editingUser) return;
    try {
      const data = { name: formData.name, email: formData.email, role: 'admin', auth_provider: formData.auth_provider, skip_2fa: formData.skip_2fa, is_active: formData.is_active };
      if (formData.password) data.password = formData.password;
      await axios.put(`${API}/users/${editingUser.id}`, data, { withCredentials: true });
      toast.success('Administrator oppdatert');
      setShowEditDialog(false); resetForm(); setEditingUser(null); fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Kunne ikkje oppdatere'); }
  };

  const handleDelete = async (au) => {
    if (au.id === user?.id) { toast.error('Du kan ikkje slette din eigen konto'); return; }
    const ok = await confirmDialog({ title: 'Slette administrator?', message: `Er du sikker på at du vil slette ${au.name || au.email}?`, confirmText: 'Ja, slett', variant: 'danger' });
    if (!ok) return;
    try {
      await axios.delete(`${API}/users/${au.id}`, { withCredentials: true });
      toast.success('Administrator sletta'); fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Kunne ikkje slette'); }
  };

  const AdminForm = ({ onSubmit, submitLabel }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs text-slate-500 mb-1 block">Namn *</Label><Input value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} className="h-10" /></div>
        <div><Label className="text-xs text-slate-500 mb-1 block">E-post *</Label><Input type="email" value={formData.email} onChange={e => setFormData(p => ({...p, email: e.target.value}))} className="h-10" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Innlogging</Label>
          <Select value={formData.auth_provider} onValueChange={v => setFormData(p => ({...p, auth_provider: v, password: v === 'microsoft' ? '' : p.password}))}>
            <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="local">E-post/passord</SelectItem>
              <SelectItem value="microsoft">Microsoft</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {formData.auth_provider === 'local' && (
          <div><Label className="text-xs text-slate-500 mb-1 block">{editingUser ? 'Nytt passord (valfritt)' : 'Passord *'}</Label><Input type="password" value={formData.password} onChange={e => setFormData(p => ({...p, password: e.target.value}))} className="h-10" placeholder={editingUser ? 'La stå tomt' : ''} /></div>
        )}
      </div>
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Checkbox id="skip2fa" checked={formData.skip_2fa} onCheckedChange={c => setFormData(p => ({...p, skip_2fa: c}))} />
          <label htmlFor="skip2fa" className="text-sm text-slate-700 cursor-pointer">Hopp over 2FA</label>
        </div>
        {editingUser && (
          <div className="flex items-center gap-2">
            <Checkbox id="isActive" checked={formData.is_active} onCheckedChange={c => setFormData(p => ({...p, is_active: c}))} />
            <label htmlFor="isActive" className="text-sm text-slate-700 cursor-pointer">Aktiv</label>
          </div>
        )}
      </div>
      <DialogFooter className="gap-2 pt-2">
        <Button variant="outline" onClick={() => { setShowAddDialog(false); setShowEditDialog(false); resetForm(); }}>Avbryt</Button>
        <Button onClick={onSubmit} className="bg-teal-600 hover:bg-teal-700">{submitLabel}</Button>
      </DialogFooter>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <AdminNavigation />
      <main className="flex-1 p-4 lg:p-8 overflow-x-hidden">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <Link to="/admin/settings"><Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500"><ArrowLeft className="w-4 h-4 mr-1" />Innstillingar</Button></Link>
          </div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Administratorar</h1>
              <p className="text-sm text-slate-500 mt-1">{admins.length} admin-brukar{admins.length !== 1 ? 'ar' : ''}</p>
            </div>
            <Button onClick={() => { resetForm(); setShowAddDialog(true); }} className="bg-teal-600 hover:bg-teal-700" size="sm">
              <UserPlus className="w-4 h-4 mr-1.5" />Ny administrator
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {admins.map((au, i) => (
                <div key={au.id} className={`flex items-center justify-between p-4 hover:bg-slate-50 transition-colors group ${i > 0 ? 'border-t border-slate-100' : ''}`} data-testid={`admin-row-${au.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-semibold">
                        {au.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2) || 'A'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{au.name || 'Ukjent'}</p>
                      <p className="text-xs text-slate-400 truncate">{au.email}</p>
                    </div>
                    <div className="flex items-center gap-1.5 ml-2">
                      {au.auth_provider === 'microsoft' && <Badge variant="outline" className="text-[10px] h-5 border-blue-200 text-blue-600 bg-blue-50">Microsoft</Badge>}
                      {au.id === user?.id && <Badge variant="outline" className="text-[10px] h-5 border-teal-200 text-teal-600 bg-teal-50">Du</Badge>}
                      {au.is_active === false && <Badge variant="outline" className="text-[10px] h-5 border-red-200 text-red-600 bg-red-50">Deaktivert</Badge>}
                      {au.skip_2fa && <Badge variant="outline" className="text-[10px] h-5 border-amber-200 text-amber-600 bg-amber-50">Utan 2FA</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(au)} className="h-8 text-xs text-slate-500 hover:text-teal-600">
                      <Edit className="w-3.5 h-3.5 mr-1" />Rediger
                    </Button>
                    {au.id !== user?.id && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(au)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle>Ny administrator</DialogTitle><DialogDescription>Opprett ein ny admin-brukar</DialogDescription></DialogHeader>
              <AdminForm onSubmit={handleAdd} submitLabel="Opprett" />
            </DialogContent>
          </Dialog>

          <Dialog open={showEditDialog} onOpenChange={v => { setShowEditDialog(v); if (!v) { setEditingUser(null); resetForm(); } }}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle>Rediger administrator</DialogTitle><DialogDescription>{editingUser?.name}</DialogDescription></DialogHeader>
              <AdminForm onSubmit={handleEdit} submitLabel="Lagre" />
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}
